/**
 * PATH: packages/ui/src/kanban/components/KanbanCardLabelPicker.tsx
 * FILE_ID: UI-KANBAN-CARD-LABEL-PICKER-001
 * PURPOSE: The body of the card hover-menu's "Colour" panel. A Trello-style
 *   coloured-LABEL toggler (Tag/CardTag, many-per-card) with fully INLINE
 *   registry editing — pencil on EACH row (rename/recolour/archive), a colour
 *   swatch on EACH row for one-click recolour, and an always-visible "New
 *   label" composer — plus the legacy single-token tile TINT strip.
 * WHY:
 *   FEAT-2026-07-04-KANBAN-LABEL-PICKER-V2 — operator on the v1 global "Edit"
 *   toggle: "The pencil should have been in EACH label … in the list I should
 *   be able to add a new label and select the color … and change the color by
 *   each label box". No modes: every affordance sits on the row it affects.
 * FLOW:
 *   ┌───────────────── label row ─────────────────┐
 *   │ [swatch]→recolour  [name]→attach/detach  [✎]→rename/archive │
 *   └─────────────────────────────────────────────┘
 *   [+ New label] → name + palette → api.createTag
 *   every registry write → labelRegistry.onChanged → tags+cards invalidate
 * DEPENDENCIES:
 *   - ./label-picker/parts: MiniPalette + InlineLabelForm (shared editor body).
 *   - ../cardColors: tint palette tokens + normaliser.
 * RELATED:
 *   - ./KanbanCardHoverMenu.tsx: renders this inside the portaled "Colour" panel.
 *   - ./card-detail/LabelManagerPanel.tsx: drawer/settings editor (palette source).
 */
import { useMemo, useState } from 'react'
import { Check, Pencil, Plus, Tag as TagIcon } from 'lucide-react'

import type { Card, Tag } from '../types'
import { normaliseCardColor } from '../cardColors'
import { MiniPalette, InlineLabelForm } from './label-picker/parts'
import { TintStrip } from './label-picker/TintStrip'
import type { KanbanCardQuickActionHandlers } from './KanbanCard.types'

export interface KanbanCardLabelPickerProps {
  card: Card
  /** The org labels (Tags) offered for toggling. */
  availableLabels: Tag[]
  /** Enables inline registry editing (per-row pencil + swatch recolour +
   *  "New label" composer). Omit → the picker stays read-only toggling. */
  labelRegistry?: KanbanCardQuickActionHandlers['labelRegistry']
  /** Attach an existing label to the card (→ api.attachTag). */
  onAttachLabel?: (tagId: string) => Promise<unknown> | void
  /** Detach a label from the card (→ api.detachTag). */
  onDetachLabel?: (tagId: string) => Promise<unknown> | void
  /** Set / clear the card's tile tint token ('' clears). Omit to hide the
   *  tint row entirely (labels-only host). */
  onSetColor?: (color: string) => Promise<unknown> | void
}

/** The set of label (tag) ids currently ON the card (active, not revoked). */
function activeLabelIds(card: Card): Set<string> {
  const ids = (card.tags ?? [])
    .filter((ct) => !ct.revoked_at)
    .map((ct) => ct.tag?.id)
    .filter((id): id is string => !!id)
  return new Set(ids)
}

type Expanded =
  | { kind: 'edit'; tagId: string }
  | { kind: 'recolour'; tagId: string }
  | { kind: 'create' }
  | null

export function KanbanCardLabelPicker({
  card,
  availableLabels,
  labelRegistry,
  onAttachLabel,
  onDetachLabel,
  onSetColor,
}: KanbanCardLabelPickerProps) {
  const [busyId, setBusyId] = useState<string | null>(null)
  const [tintBusy, setTintBusy] = useState(false)
  const [expanded, setExpanded] = useState<Expanded>(null)
  const [regBusy, setRegBusy] = useState(false)
  const [regError, setRegError] = useState<string | null>(null)
  const selected = useMemo(() => activeLabelIds(card), [card])
  const currentTint = normaliseCardColor(card.color)
  const labelsEditable = !!onAttachLabel && !!onDetachLabel

  async function toggleLabel(tag: Tag) {
    if (!labelsEditable) return
    const on = selected.has(tag.id)
    setBusyId(tag.id)
    try {
      await (on ? onDetachLabel!(tag.id) : onAttachLabel!(tag.id))
    } finally {
      setBusyId(null)
    }
  }

  async function setTint(token: string) {
    if (!onSetColor) return
    setTintBusy(true)
    try {
      await onSetColor(token)
    } finally {
      setTintBusy(false)
    }
  }

  /** One write path for every registry mutation: run, refresh, collapse. */
  async function registryWrite(run: () => Promise<unknown>) {
    if (!labelRegistry) return
    setRegBusy(true)
    setRegError(null)
    try {
      await run()
      labelRegistry.onChanged()
      setExpanded(null)
    } catch (e) {
      setRegError(e instanceof Error ? e.message : 'Could not save the label.')
    } finally {
      setRegBusy(false)
    }
  }

  // Creation needs the org id (labels are org-scoped rows); registries wired
  // without one (shouldn't happen on boards) just hide the composer.
  const canCreate = !!labelRegistry && labelRegistry.orgId !== undefined
  const createLabel = (draft: { name: string; color: string }) =>
    registryWrite(() =>
      labelRegistry!.api.createTag({
        organisation: labelRegistry!.orgId!,
        name: draft.name,
        path: draft.name.toLowerCase().replace(/\s+/g, '-'),
        color: draft.color,
      }),
    )

  return (
    <div className="flex w-64 flex-col gap-2 p-2" data-testid="kanban-card-label-picker">
      {/* Labels (Tags) — many per card. Selected ones also colour the card's
          right-edge identity accent, so "colours are also labels". */}
      <div>
        <div className="mb-1 flex items-center gap-1.5 px-0.5 text-[11px] font-semibold uppercase tracking-wide text-neutral-500 dark:text-zinc-400">
          <TagIcon className="h-3 w-3" aria-hidden="true" /> Labels
        </div>
        {availableLabels.length === 0 && !labelRegistry && (
          <p className="px-0.5 py-1 text-[11px] leading-snug text-neutral-400 dark:text-zinc-500">
            No labels yet. Create labels for this board to colour-code cards.
          </p>
        )}
        <div className="flex max-h-56 flex-col gap-0.5 overflow-y-auto pr-0.5">
          {availableLabels.map((tag) => {
            const on = selected.has(tag.id)
            const swatch = tag.color || '#94a3b8'
            const isEditing = expanded?.kind === 'edit' && expanded.tagId === tag.id
            const isRecolouring = expanded?.kind === 'recolour' && expanded.tagId === tag.id
            return (
              <div key={tag.id}>
                <div
                  data-testid={`kanban-card-label-${tag.id}`}
                  className={[
                    'group/lrow flex w-full items-center gap-2 rounded-md px-1.5 py-1 text-[12.5px]',
                    'transition-colors hover:bg-neutral-100 dark:hover:bg-zinc-800',
                    on ? 'bg-neutral-100 dark:bg-zinc-800' : '',
                  ].join(' ')}
                >
                  {/* Swatch — with a registry it's a button: one click opens the
                      palette, one more click recolours THIS label everywhere. */}
                  {labelRegistry ? (
                    <button
                      type="button"
                      data-testid={`kanban-card-label-swatch-${tag.id}`}
                      aria-label={`Change colour of ${tag.name}`}
                      aria-expanded={isRecolouring}
                      title={`${tag.name} — change colour`}
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        setRegError(null)
                        setExpanded(isRecolouring ? null : { kind: 'recolour', tagId: tag.id })
                      }}
                      className="h-4 w-4 shrink-0 rounded-sm ring-1 ring-black/10 transition-transform hover:scale-110 dark:ring-white/15"
                      style={{
                        background: swatch,
                        boxShadow: tag.accent_color ? `inset 0 0 0 1.5px ${tag.accent_color}` : undefined,
                      }}
                    />
                  ) : (
                    <span
                      className="h-3.5 w-3.5 shrink-0 rounded-sm ring-1 ring-black/10 dark:ring-white/15"
                      style={{
                        background: swatch,
                        boxShadow: tag.accent_color ? `inset 0 0 0 1.5px ${tag.accent_color}` : undefined,
                      }}
                      aria-hidden="true"
                    />
                  )}
                  {/* Name — click toggles the label on/off the card. */}
                  <button
                    type="button"
                    disabled={!labelsEditable || busyId === tag.id}
                    data-testid={`kanban-card-label-toggle-${tag.id}`}
                    aria-pressed={on}
                    title={tag.path || tag.name}
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      void toggleLabel(tag)
                    }}
                    className="flex min-w-0 flex-1 items-center gap-2 text-left text-neutral-700 disabled:opacity-50 dark:text-zinc-200"
                  >
                    <span className="min-w-0 flex-1 truncate">{tag.name}</span>
                    {on && (
                      <Check className="h-3.5 w-3.5 shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden="true" />
                    )}
                  </button>
                  {/* Pencil — per-row rename/recolour/archive editor. */}
                  {labelRegistry && (
                    <button
                      type="button"
                      data-testid={`kanban-card-label-edit-${tag.id}`}
                      aria-label={`Edit label ${tag.name}`}
                      aria-expanded={isEditing}
                      title="Edit label"
                      onMouseDown={(e) => e.stopPropagation()}
                      onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        setRegError(null)
                        setExpanded(isEditing ? null : { kind: 'edit', tagId: tag.id })
                      }}
                      className={[
                        'shrink-0 rounded p-0.5 transition-colors',
                        isEditing
                          ? 'bg-blue-600 text-white'
                          : 'text-neutral-300 hover:bg-neutral-200 hover:text-neutral-600 group-hover/lrow:text-neutral-400 dark:text-zinc-600 dark:hover:bg-zinc-700 dark:hover:text-zinc-200 dark:group-hover/lrow:text-zinc-500',
                      ].join(' ')}
                    >
                      <Pencil className="h-3 w-3" aria-hidden="true" />
                    </button>
                  )}
                </div>
                {/* Inline expansions, directly under the row they affect. */}
                {isRecolouring && labelRegistry && (
                  <div className="px-1.5 py-1" data-testid={`kanban-card-label-recolour-${tag.id}`}>
                    <MiniPalette
                      value={tag.color || ''}
                      busy={regBusy}
                      testIdPrefix={`kanban-card-label-recolour-${tag.id}`}
                      onPick={(c) => void registryWrite(() => labelRegistry.api.updateTag(tag.id, { color: c }))}
                    />
                  </div>
                )}
                {isEditing && labelRegistry && (
                  <div className="px-1 py-1">
                    <InlineLabelForm
                      initial={{ name: tag.name, color: tag.color || '' }}
                      submitLabel="Save"
                      busy={regBusy}
                      testIdPrefix={`kanban-card-label-editform-${tag.id}`}
                      onSubmit={(draft) =>
                        void registryWrite(() => labelRegistry.api.updateTag(tag.id, { name: draft.name, color: draft.color }))
                      }
                      onCancel={() => setExpanded(null)}
                      onArchive={() =>
                        void registryWrite(() => labelRegistry.api.updateTag(tag.id, { archived: true }))
                      }
                    />
                  </div>
                )}
              </div>
            )
          })}
        </div>
        {/* "New label" — always visible with a registry; expands to name +
            colour palette right where the list ends. */}
        {canCreate && (
          expanded?.kind === 'create' ? (
            <div className="px-1 pt-1">
              <InlineLabelForm
                initial={{ name: '', color: '' }}
                submitLabel="Create"
                busy={regBusy}
                testIdPrefix="kanban-card-label-create"
                onSubmit={(draft) => void createLabel(draft)}
                onCancel={() => setExpanded(null)}
              />
            </div>
          ) : (
            <button
              type="button"
              data-testid="kanban-card-label-new"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                setRegError(null)
                setExpanded({ kind: 'create' })
              }}
              className="mt-0.5 flex w-full items-center gap-1.5 rounded-md px-1.5 py-1 text-left text-[12px] font-medium text-blue-700 hover:bg-blue-50 dark:text-blue-300 dark:hover:bg-blue-950/30"
            >
              <Plus className="h-3 w-3" aria-hidden="true" /> New label
            </button>
          )
        )}
        {regError && (
          <p className="px-1 pt-1 text-[11px] text-red-600 dark:text-red-400" data-testid="kanban-card-label-error">
            {regError}
          </p>
        )}
      </div>

      {/* Tile tint — the legacy single-token card background colour, kept so
          nothing is lost (operator: colours should ALSO be labels, not instead). */}
      {onSetColor && (
        <TintStrip currentTint={currentTint} busy={tintBusy} onPick={(t) => void setTint(t)} />
      )}
    </div>
  )
}
